// src/layers/imagery/groupFootprintsIntoSeries.js
//
// Turn the /imagery/footprints FeatureCollection into Series[] for the
// resolver. Identity = (slug, sku_code). Within a series, acquisitions are
// sorted ascending by acquisition_date so snapToAcquisition can walk them.
//
// Inputs:
//   - GeoJSON FeatureCollection from authz-api (/imagery/footprints)
//
// Outputs:
//   - Series[] matching the shape defined in ./types.js
//
// Notes:
//   - The endpoint currently only returns entitled, active datasets, so every
//     resulting Series has a non-null `entitlement`. When we add not-entitled
//     surfacing for the upsell flow, this function will see entitlement:null
//     features and pass them through unchanged.
//   - We compute bounds for each acquisition from the GeoJSON coverage_bounds.
//     If coverage_bounds is missing, bounds defaults to null and the UI just
//     hides the zoom-to-layer affordance for that acquisition.
//   - When /imagery/footprints is called with operating_aoi=<uuid>, each
//     feature carries a `counties` property with per-county coverage against
//     the AOI. We pass it through verbatim on each Acquisition and roll it
//     up to a series-level `counties` using max coverage ratio per county.
//     When operating_aoi is not provided, `counties` is null at both levels
//     and downstream UI should hide the coverage display.

import { makeSeriesKey } from "./resolveImageryStack";

/** @typedef {import('./types').Series} Series */
/** @typedef {import('./types').Acquisition} Acquisition */
/** @typedef {import('./types').Entitlement} Entitlement */
/** @typedef {import('./types').CountyCoverage} CountyCoverage */

/**
 * @param {object} featureCollection  GeoJSON FeatureCollection from /imagery/footprints
 * @returns {Series[]}
 */
export function groupFootprintsIntoSeries(featureCollection) {
  if (!featureCollection?.features?.length) return [];

  /** @type {Map<string, Series>} */
  const seriesMap = new Map();

  for (const feature of featureCollection.features) {
    const p = feature.properties;
    if (!p) continue;

    // Skip features missing required fields. Better to drop than to render
    // a broken series.
    if (!p.slug || !p.sku_code || !p.acquisition_date || !p.dataset_key) {
      continue;
    }

    const seriesKey = makeSeriesKey(p.slug, p.sku_code);

    let series = seriesMap.get(seriesKey);
    if (!series) {
      series = {
        series_key: seriesKey,
        slug: p.slug,
        sku_code: p.sku_code,
        basin: p.basin ?? "",
        region: p.region ?? "",
        resolution_cm: p.resolution_cm ?? null,
        display_name: buildDisplayName(p),
        acquisitions: [],
        entitlement: normalizeEntitlement(p.entitlement),
        counties: null, // filled in by the post-loop pass
      };
      seriesMap.set(seriesKey, series);
    }

    series.acquisitions.push({
      dataset_id: p.dataset_id,
      dataset_key: p.dataset_key,
      acquisition_date: p.acquisition_date,
      minzoom: p.minzoom ?? 0,
      maxzoom: p.maxzoom ?? 22,
      bounds: extractBounds(p.coverage_bounds),
      counties: extractCounties(p),
      // THE DISSOLVED FOOTPRINT, CARRIED RATHER THAN DISCARDED. `bounds` above
      // is the dataset ENVELOPE and answers a different question: it is where
      // the collect is, not where it has pixels. On this corpus the envelope is
      // ~12x the real coverage, so "is there imagery at this point" cannot be
      // answered from it.
      //
      // This is the geometry the fetcher now requests with lod=fine, and it is
      // what lets a caller test a viewport against a date's ACTUAL coverage
      // client-side. Null when the API shipped no geometry, which callers must
      // treat as "unknown", never as "no coverage" — a missing footprint and an
      // empty one are different states and only one of them means blank map.
      //
      // FROM THE FEATURE, NOT FROM `p`. Every other field here comes out of
      // feature.properties, and geometry is the one that does not: authz-api
      // builds `{ type: "Feature", id, geometry, properties }` (index.js:898),
      // so `p.geometry` is undefined and reading it silently yields null. That
      // shipped, and the symptom was every date in the strip reporting
      // "Coverage unknown" — the null-is-unknown path doing exactly what it
      // promised, over a bug one line above it.
      geometry: feature.geometry ?? null,
    });
  }

  // Sort each series' acquisitions ascending by date (resolver requires
  // this), then roll up per-acquisition counties to a series-level summary.
  for (const series of seriesMap.values()) {
    series.acquisitions.sort((a, b) =>
      a.acquisition_date.localeCompare(b.acquisition_date)
    );
    series.counties = rollUpCounties(series.acquisitions);
  }

  return Array.from(seriesMap.values());
}

/**
 * Build a human-readable display name. "Permian Midland — Basemap 10cm".
 * Mostly cosmetic; the UI can override if it wants a different format.
 */
function buildDisplayName(p) {
  const basin = capitalize(p.basin);
  const region = capitalize(p.region);
  const sku = formatSku(p.sku_code);
  return `${basin} ${region} — ${sku}`;
}

function capitalize(s) {
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * BASEMAP_10CM → "Basemap 10cm". Falls back to the raw code for unknown SKUs.
 */
function formatSku(skuCode) {
  const map = {
    BASEMAP_10CM: "Basemap 10cm",
    HIRES_5CM: "Hi-res 5cm",
    NDVI: "NDVI",
    GAS_LEAK: "Gas Leak",
    DEM: "DEM",
    BASEMAP_REFRESH_QTR: "Basemap Refresh (Quarterly)",
  };
  return map[skuCode] ?? skuCode;
}

/**
 * Normalize the entitlement record from the API. Returns null if essential
 * fields are missing, which the resolver treats as 'not_entitled'.
 */
function normalizeEntitlement(raw) {
  if (!raw || !raw.scope || !raw.status) return null;

  return {
    scope: raw.scope === "aoi" ? "aoi" : "full",
    coverage_aoi_id: raw.coverage_aoi_id ?? null,
    valid_from: raw.valid_from ?? null,
    valid_to: raw.valid_to ?? null,
    status: raw.status,
  };
}

/**
 * The bounding box of a GeoJSON geometry, as [[minLng, minLat], [maxLng, maxLat]].
 *
 * Accepts either a Feature or a bare geometry, and walks the coordinate tree to
 * whatever depth it has, so Polygon and MultiPolygon are the same code path with
 * no type switch. Returns null for a falsy input, for a geometry with no
 * `coordinates`, or when nothing numeric was found -- callers must treat null as
 * "unknown", never as an empty extent.
 *
 * NESTED PAIRS, NOT A FLAT BBOX. MapView's zoom handler wants a flat
 * [w, s, e, n] and validates with `length !== 4` before RETURNING SILENTLY
 * (MapView.jsx:1600, confirmed 2026-09-07), so a caller that forwards this
 * return value unflattened gets a control that does nothing at all rather than
 * an error. Flatten at the call site.
 *
 * A second implementation of the same thing lives at MapView.jsx:82
 * (`boundsFromGeometry`, confirmed 2026-09-07). This one is kept separate so the
 * module has no UI dependency. An earlier version of this comment pointed at a
 * `boundsFromGeom` in LayerPanel.jsx, which does not exist -- that is why the
 * two live citations above carry the date they were checked.
 */
function extractBounds(geom) {
  if (!geom) return null;

  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;

  const processCoord = ([x, y]) => {
    if (typeof x !== "number" || typeof y !== "number") return;
    minX = Math.min(minX, x);
    minY = Math.min(minY, y);
    maxX = Math.max(maxX, x);
    maxY = Math.max(maxY, y);
  };

  const walk = (coords) => {
    if (!Array.isArray(coords)) return;
    if (typeof coords[0] === "number") processCoord(coords);
    else coords.forEach(walk);
  };

  const geometry = geom.type === "Feature" ? geom.geometry : geom;
  if (!geometry?.coordinates) return null;

  walk(geometry.coordinates);

  if (!isFinite(minX)) return null;
  return [[minX, minY], [maxX, maxY]];
}

/**
 * Pass per-acquisition counties through verbatim.
 *
 * Returns null when the feature has no counties property (operating_aoi
 * was not in the request). Returns the array as-is when present, even
 * if empty — the empty-array case preserves the "AOI provided but
 * acquisition touches no AOI counties" signal, which is distinct from
 * "no AOI context at all" (null).
 *
 * Downstream UI should treat null as "hide coverage display" and an
 * empty array as "AOI provided, this acquisition has no relevant
 * coverage" (which today shouldn't happen since the server-side join
 * filters out acquisitions with no AOI overlap, but the shape preserves
 * the distinction in case that filter changes).
 */
function extractCounties(p) {
  if (!Array.isArray(p.counties)) return null;
  return p.counties;
}

/**
 * Series-level rollup: max coverage ratio per county across all
 * acquisitions in the series.
 *
 * Returns null if no acquisition has county data (no AOI context).
 * Returns CountyCoverage[] sorted by aoi_coverage_ratio desc, then
 * county name asc.
 *
 * Rollup answers "what's the best coverage this series gives me in
 * <county>?" — the natural question for a discovery surface. The
 * expanded acquisition pill strip will show per-acquisition detail
 * for users who want to see how coverage varies date by date.
 */
export function rollUpCounties(acquisitions) {
  const anyHaveCounties = acquisitions.some((a) => a.counties !== null);
  if (!anyHaveCounties) return null;

  // For each county_key, keep the row with the highest aoi_coverage_ratio.
  // Ties broken by county_coverage_ratio, then by county name (for stable
  // display order across loads).
  const best = new Map(); // county_key -> CountyCoverage row

  for (const acq of acquisitions) {
    if (!acq.counties) continue;
    for (const row of acq.counties) {
      const current = best.get(row.county_key);

      if (!current) {
        best.set(row.county_key, row);
        continue;
      }

      // Treat null ratios as 0 for comparison. A null aoi_coverage_ratio
      // means zero AOI overlap, which the SQL filter should have excluded,
      // but defensive code is cheap.
      const newRatio = row.aoi_coverage_ratio ?? 0;
      const curRatio = current.aoi_coverage_ratio ?? 0;

      if (newRatio > curRatio) {
        best.set(row.county_key, row);
      } else if (newRatio === curRatio) {
        const newCounty = row.county_coverage_ratio ?? 0;
        const curCounty = current.county_coverage_ratio ?? 0;
        if (newCounty > curCounty) {
          best.set(row.county_key, row);
        }
      }
    }
  }

  const rows = Array.from(best.values());

  rows.sort((a, b) => {
    const ar = a.aoi_coverage_ratio ?? 0;
    const br = b.aoi_coverage_ratio ?? 0;
    if (br !== ar) return br - ar;
    return a.county.localeCompare(b.county);
  });

  return rows;
}
